import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import axios from 'axios';
import { ArrowRight, Building2, Construction, CheckCircle, Award, Clock, MapPin, ChevronLeft, ChevronRight, Star, Quote, X, Gift, PlayCircle, Video } from 'lucide-react';
import { useSettings } from '@/context/SettingsContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const { settings } = useSettings();
  const [projects, setProjects] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [videoTestimonials, setVideoTestimonials] = useState([]);
  const [offers, setOffers] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  useEffect(() => {
    fetchProjects();
    fetchTestimonials();
    fetchVideoTestimonials();
    fetchOffers();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching testimonials:', error);
    }
  };

  const fetchVideoTestimonials = async () => {
    try {
      const response = await axios.get(`${API}/video-testimonials`);
      setVideoTestimonials(response.data);
    } catch (error) {
      console.error('Error fetching video testimonials:', error);
    }
  };

  const fetchOffers = async () => {
    try {
      const response = await axios.get(`${API}/offers`);
      setOffers(response.data.filter(o => o.show_on_homepage));
    } catch (error) {
      console.error('Error fetching offers:', error);
    }
  };

  const openOfferModal = (offer) => {
    setSelectedOffer(offer);
    setShowOfferModal(true);
  };

  const stats = [
    { icon: <Award size={32} />, number: settings.stat_1_number, label: settings.stat_1_label },
    { icon: <Building2 size={32} />, number: settings.stat_2_number, label: settings.stat_2_label },
    { icon: <CheckCircle size={32} />, number: settings.stat_3_number, label: settings.stat_3_label },
  ];

  const upcomingProjects = projects.filter(p => p.status === 'upcoming').slice(0, 3);
  const ongoingProjects = projects.filter(p => p.status === 'ongoing').slice(0, 3);
  const completedProjects = projects.filter(p => p.status === 'completed').slice(0, 3);

  const ProjectCarousel = ({ projectList, title }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    const next = () => {
      setCurrentIndex((prev) => (prev + 1) % projectList.length);
    };

    const prev = () => {
      setCurrentIndex((prev) => (prev - 1 + projectList.length) % projectList.length);
    };

    if (projectList.length === 0) return null;

    return (
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="section-title">{title}</h2>
            </div>
            <div className="flex gap-3">
              <button onClick={prev} className="carousel-arrow" data-testid={`${title}-prev`}>
                <ChevronLeft size={20} />
              </button>
              <button onClick={next} className="carousel-arrow" data-testid={`${title}-next`}>
                <ChevronRight size={20} />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projectList.slice(currentIndex, currentIndex + 3).map((project) => (
              <div key={project.id} className="project-carousel-card" data-testid={`carousel-project-${project.id}`}>
                <div className="relative h-72 bg-gray-100 flex items-center justify-center overflow-hidden">
                  {project.images && project.images.length > 0 ? (
                    <img src={project.images[0]} alt={project.name} className="w-full h-full object-contain" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                      <Building2 size={64} className="text-gray-400" />
                    </div>
                  )}
                  <div className="project-overlay">
                    <span className={`status-badge status-${project.status} mb-2`}>
                      {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
                    </span>
                    <h3>{project.name}</h3>
                    <p className="flex items-center gap-1">
                      <MapPin size={14} />
                      {project.location_area}, {project.location_city}
                    </p>
                    <p className="text-sm">{project.apartment_types?.join(', ')}</p>
                    <Link to={`/projects/${project.id}`} className="know-more-btn">
                      Know More <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <section className="hero-banner">
        <div className="hero-content h-full flex items-center justify-center">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="hero-heading">
              {settings.hero_title}
            </h1>
            <p className="text-lg sm:text-xl text-white mb-10 opacity-95">
              {settings.hero_subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/projects" className="cta-button-primary" data-testid="view-projects-btn">
                View Projects
              </Link>
              <Link to="/contact" className="cta-button-secondary" data-testid="schedule-visit-btn">
                Schedule a Site Visit
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="stats-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card" data-testid={`stat-${index}`}>
                <div className="stat-icon">{stat.icon}</div>
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Special Offers Banner */}
      {offers.length > 0 && (
        <section className="py-12 bg-gradient-to-r from-purple-600 to-pink-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold text-white flex items-center justify-center gap-2">
                <Gift size={28} />
                Special Offers
              </h2>
              <p className="text-purple-100 mt-2">Limited time festive deals</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {offers.slice(0, 3).map((offer) => (
                <div 
                  key={offer.id}
                  className="bg-white rounded-xl overflow-hidden shadow-lg cursor-pointer transform hover:scale-105 transition-transform"
                  onClick={() => openOfferModal(offer)}
                  data-testid={`offer-card-${offer.id}`}
                >
                  {offer.flyer_image ? (
                    <div className="h-40 bg-gray-100">
                      <img 
                        src={offer.flyer_image} 
                        alt={offer.title}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-purple-100 to-pink-100 flex items-center justify-center">
                      <Gift size={48} className="text-purple-400" />
                    </div>
                  )}
                  <div className="p-4">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-700 rounded-full mb-2">
                      {offer.occasion}
                    </span>
                    <h3 className="font-semibold text-gray-900 mb-1">{offer.title}</h3>
                    {offer.discount_text && (
                      <p className="text-lg font-bold text-red-600">{offer.discount_text}</p>
                    )}
                    <p className="text-xs text-gray-500 mt-2">Valid till: {offer.valid_until}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {upcomingProjects.length > 0 && <ProjectCarousel projectList={upcomingProjects} title="New Launches" />}
      {ongoingProjects.length > 0 && <ProjectCarousel projectList={ongoingProjects} title="Ongoing Projects" />}
      {completedProjects.length > 0 && <ProjectCarousel projectList={completedProjects} title="Completed Projects" />}

      <section className="about-section py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="section-title">About Us</h2>
            <p className="section-subtitle">The Spirit Of Quality Living</p>
          </div>
          <div className="max-w-4xl mx-auto">
            <p className="text-gray-700 leading-relaxed text-center mb-8">
              {settings.about_text}
            </p>
            <div className="text-center">
              <Link to="/why-us" className="cta-button-primary" data-testid="learn-more-btn">
                Read More
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="section-title">What Our Customers Say</h2>
              <p className="section-subtitle">Testimonials from Happy Homeowners</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.slice(0, 6).map((testimonial, index) => (
                <div 
                  key={testimonial.id || index} 
                  className="bg-gray-50 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                  data-testid={`testimonial-card-${index}`}
                >
                  {testimonial.customer_photo && (
                    <div 
                      className="cursor-pointer bg-gray-100"
                      onClick={() => setLightboxImage(testimonial.customer_photo)}
                    >
                      <img 
                        src={testimonial.customer_photo} 
                        alt={`${testimonial.customer_name} - Key Handover`}
                        className="w-full h-auto max-h-64 object-contain"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-1 mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i} 
                          size={18} 
                          className={i < testimonial.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} 
                        />
                      ))}
                    </div>
                    <div className="relative mb-4">
                      <Quote size={24} className="absolute -top-2 -left-2 text-gray-200" />
                      <p className="text-gray-700 leading-relaxed pl-4 italic">
                        "{testimonial.testimonial_text}"
                      </p>
                    </div>
                    <div className="border-t pt-4 mt-4">
                      <p className="font-semibold text-gray-900">{testimonial.customer_name}</p>
                      <p className="text-sm text-gray-500">Verified Buyer - {testimonial.project_name}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Customer Speaks - Video Testimonials */}
      {settings.show_video_testimonials !== false && videoTestimonials.length > 0 && (
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white" data-testid="video-testimonials-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-red-50 text-red-600 rounded-full text-sm font-medium mb-4">
                <Video size={16} />
                {settings.video_testimonials_title || 'Customer Speaks'}
              </div>
              <h2 className="section-title">Hear It From Our Homeowners</h2>
              <p className="section-subtitle">
                {settings.video_testimonials_subtitle || 'Watch what our happy homeowners have to say'}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {videoTestimonials.slice(0, 6).map((video, index) => (
                <div
                  key={video.id || index}
                  className="group relative bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => setPlayingVideo(video)}
                  data-testid={`video-testimonial-${index}`}
                >
                  <div className="aspect-video bg-gray-100 relative overflow-hidden">
                    {video.thumbnail_url ? (
                      <img
                        src={video.thumbnail_url}
                        alt={video.customer_name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-gray-200 to-gray-300">
                        <Video size={48} className="text-gray-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black bg-opacity-30 group-hover:bg-opacity-50 transition-all flex items-center justify-center">
                      <PlayCircle size={68} className="text-white opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all drop-shadow-lg" />
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-1">{video.customer_name}</h3>
                    <p className="text-sm text-gray-500 mb-2">Verified Buyer - {video.project_name}</p>
                    {video.description && (
                      <p className="text-sm text-gray-600 line-clamp-2">{video.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16 bg-gradient-to-br from-gray-800 to-gray-900 text-white text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Find Your Dream Home?</h2>
          <p className="text-lg mb-8 opacity-90">
            Get in touch with our team today and let us help you find the perfect home in Nagpur
          </p>
          <Link to="/contact" className="cta-button-primary">
            Contact Us Now
          </Link>
        </div>
      </section>

      <Footer />

      {/* Video Player Modal */}
      {playingVideo && (
        <div
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={() => setPlayingVideo(null)}
          data-testid="video-player-modal"
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
            onClick={() => setPlayingVideo(null)}
            data-testid="close-video-btn"
          >
            <X size={32} />
          </button>
          <div
            className="w-full max-w-5xl aspect-video bg-black rounded-lg overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {playingVideo.video_url && playingVideo.video_url.includes('youtube.com/embed') ? (
              <iframe
                src={`${playingVideo.video_url}?autoplay=1&rel=0`}
                title={playingVideo.customer_name}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : playingVideo.video_url ? (
              <video
                src={playingVideo.video_url}
                controls
                autoPlay
                className="w-full h-full"
              />
            ) : null}
          </div>
          {(playingVideo.customer_name || playingVideo.project_name) && (
            <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-center text-white">
              <p className="font-semibold text-lg">{playingVideo.customer_name}</p>
              <p className="text-sm opacity-80">{playingVideo.project_name}</p>
            </div>
          )}
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={lightboxImage} 
            alt="Full size view"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Offer Detail Modal */}
      {showOfferModal && selectedOffer && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
          onClick={() => setShowOfferModal(false)}
        >
          <div 
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              <button 
                className="absolute top-4 right-4 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors z-10"
                onClick={() => setShowOfferModal(false)}
              >
                <X size={20} />
              </button>
              {selectedOffer.flyer_image && (
                <div className="bg-gray-100">
                  <img 
                    src={selectedOffer.flyer_image} 
                    alt={selectedOffer.title}
                    className="w-full h-auto max-h-96 object-contain"
                  />
                </div>
              )}
            </div>
            <div className="p-6">
              <span className="inline-block px-3 py-1 text-sm font-medium bg-purple-100 text-purple-700 rounded-full mb-3">
                {selectedOffer.occasion}
              </span>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{selectedOffer.title}</h2>
              {selectedOffer.discount_text && (
                <p className="text-xl font-bold text-red-600 mb-4">{selectedOffer.discount_text}</p>
              )}
              {selectedOffer.description && (
                <p className="text-gray-600 mb-4">{selectedOffer.description}</p>
              )}
              <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
                <Clock size={16} />
                <span>Valid from {selectedOffer.valid_from} to {selectedOffer.valid_until}</span>
              </div>
              {selectedOffer.terms_conditions && (
                <p className="text-xs text-gray-400 mb-6">*{selectedOffer.terms_conditions}</p>
              )}
              <Link
                to={selectedOffer.cta_link || '/contact'}
                className="block w-full text-center bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors"
                onClick={() => setShowOfferModal(false)}
              >
                {selectedOffer.cta_text || 'Enquire Now'}
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;